import { API_STATUS } from 'core-lib/constants/network';
import { SessionStorage } from 'core-lib/constants/storage';
import { fetchAddressFromLatLng } from 'core-lib/services/fetchAddressFromLatLng';
import { AddressStatusType } from 'core-lib/types/Address';
import { getImageKitUrl } from 'core-lib/utils/imageKit';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Form from 'testDrive/components/Form';
import ServicablePopup from 'testDrive/components/popups/ServicablePopup';
import { LeadGenerationSource, LeadType } from 'testDrive/constants';
import { useCheckServiceable } from 'testDrive/hooks/useCheckServiceable';
import { useFormDetails } from 'testDrive/hooks/useFormDetails';
import { useServiceability } from 'testDrive/hooks/useServiceability';
import { useTestDriveLocation } from 'testDrive/hooks/useTestDriveLocation';
import { fetchLeadDetails } from 'testDrive/services/fetchLeadDetails';
import {
  updateDealer,
  updateFormDetails,
  updateLocation,
  updateNoRedirect,
  updateServiceablePopup,
  updateWebSuccess,
} from 'testDrive/store';
import { AnimatedImage, Button, Modal, Typography } from 'ui';
import MaterialSpinner from 'ui/components/MaterialSpinner';
import s from './TestDrivePopup.module.scss';

interface TestDrivePopupProps {
  modelId: number;
  show: boolean;
  onClose: () => void;
}

const TestDrivePopup = ({ modelId, show, onClose }: TestDrivePopupProps) => {
  const { serviceableStatus } = useServiceability();
  const { checkServiceable } = useCheckServiceable();
  const { address } = useTestDriveLocation();
  const [data, setData] = useState(null);
  const [apiStatus, setApisStatus] = useState(API_STATUS.default);
  const dealerDetails = useSelector(
    (state: any) => state?.testDrive?.dealerDetails
  );
  const showServiceablePopup = useSelector(
    (state: any) => state?.testDrive?.showServiceablePopup
  );
  const showWebSuccess = useSelector(
    (state) => state?.testDrive?.showWebSuccess
  );
  const dispatch = useDispatch();

  const leadGenerationSource = useMemo(() => {
    const leadSource = sessionStorage.getItem(SessionStorage.utmSource);
    if (leadSource) {
      return leadSource;
    }
    return LeadGenerationSource.Website;
  }, []);

  const { handleChange, handleSubmit, form, generatingLead } = useFormDetails({
    userDetails: data?.userDetails,
    address: address,
    pageName: 'new_cars_web',
    replace: false,
    section: 'new_cars_web',
    dealer: dealerDetails,
    data: data,
    fireGTMEvent: true,
    leadGenerationSource: leadGenerationSource as any,
  });

  const getData = async (modelId) => {
    setApisStatus(API_STATUS.in_progress);
    const response = await fetchLeadDetails(modelId, true, true);
    if (response) {
      setData(response);
      setApisStatus(API_STATUS.success);
    } else {
      setApisStatus(API_STATUS.failure);
    }
  };

  const handleCityChange = async (location) => {
    const { locationData, error } = await fetchAddressFromLatLng({
      lat: location?.latitude,
      lng: location?.longitude,
      radius: 100,
    });
    let city: any = null;
    if (locationData) {
      city = {
        latitude: locationData?.lat,
        longitude: locationData?.lng,
        state: locationData?.state,
        locality: locationData?.locality,
        city: locationData?.city,
        pincode: locationData?.pincode,
        country: null,
        infraCity: locationData?.infraCity
          ? locationData?.infraCity
          : locationData?.city,
        zoneName: locationData?.zoneName
          ? locationData?.zoneName
          : locationData?.city,
        stateId: locationData?.stateId,
      };
    }
    if (city?.city && city?.latitude && city?.longitude) {
      dispatch(
        updateLocation({
          data: city,
          status: AddressStatusType.Success,
        })
      );
      if (city?.pincode) {
        handleChange('pincode', `${city.pincode}`);
      }
      if (city?.city) {
        handleChange('city', city.city);
      }

      const isServiceable = checkServiceable({
        city: city?.city,
        leadType: data?.leadType,
      });
      if (!isServiceable) {
        dispatch(updateFormDetails({ ...form }));
        dispatch(updateServiceablePopup(true));
      }
    }
    handleChange('dealer_name', '');
    dispatch(updateDealer(null));
  };

  const handleClose = () => {
    dispatch(updateNoRedirect(false));
    dispatch(updateWebSuccess(false));
    onClose();
  };

  useEffect(() => {
    if (modelId) {
      dispatch(updateNoRedirect(true));
      getData(modelId);
    }
  }, [modelId]);

  return (
    <Modal
      show={show}
      isWeb
      head={{ show: true, onClose: () => handleClose() }}
      rootStyle={{ zIndex: 2 }}
    >
      <div className={s.root}>
        {data && !data?.isBooked && !showWebSuccess && (
          <div>
            <Form
              form={form}
              data={data}
              handleChange={(name, value) => {
                handleChange(name, value);
              }}
              showDealerInput={data?.showDealerInput}
              showEmailInput={data?.leadType === LeadType.Oem}
              handleCityChange={(location) => {
                handleCityChange(location);
              }}
              address={address}
              locationPopupVariant="fullScreenPopup"
              isWeb
            />
            <div className={s.footer}>
              <Button
                label="Request a callback"
                onClick={(e) => {
                  const isServiceable = checkServiceable({
                    city: address?.data?.city,
                    leadType: data?.leadType,
                  });
                  if (!isServiceable) {
                    dispatch(updateFormDetails({ ...form }));
                    dispatch(updateServiceablePopup(true));
                  } else {
                    handleSubmit(e);
                    dispatch(updateFormDetails({ ...form }));
                  }
                }}
                fullwidth
                isDisabled={[API_STATUS.in_progress].includes(generatingLead)}
                isLoading={[API_STATUS.in_progress].includes(generatingLead)}
              />
              <Typography
                label="Don’t worry! We won’t spam you"
                variant="small_m"
                color="text_subheading"
                style={{
                  textAlign: 'center',
                  marginTop: '16px',
                  padding: '8px 0px',
                }}
              />
            </div>
          </div>
        )}

        {((data && data?.isBooked) || showWebSuccess) && (
          <div className={s.success}>
            <AnimatedImage src="/img/newCars/success.gif" />
            <Typography
              label="Thank you for booking a test drive!"
              variant="h3"
            />
            <Typography
              label="You will get a call back within 5 minutes"
              variant="h4_light"
              color="text_subheading"
            />
          </div>
        )}
        {[API_STATUS.default, API_STATUS.in_progress].includes(apiStatus) && (
          <div className={s.loader}>
            <MaterialSpinner width={40} height={40} />
          </div>
        )}
        {[API_STATUS.failure].includes(apiStatus) && (
          <div className={s.error}>
            <AnimatedImage
              src={getImageKitUrl({
                product: 'ImageKit',
                imageUrl:
                  'https://parkplus-bkt-img.parkplus.io/production/vehicle/public/car_20230829194736577083.png',
              })}
              className={s.image}
            />
            <Typography
              label="Uh-Oh"
              variant="h1"
              style={{ paddingTop: '24px' }}
            />
            <Typography
              label={`We are unable to fetch details at the moment. \nPlease try again later`}
              variant="body_para_r"
              color="text_subheading"
              style={{ paddingTop: '4px', whiteSpace: 'break-spaces' }}
            />
          </div>
        )}
        <ServicablePopup
          show={showServiceablePopup}
          onClose={() => {
            dispatch(updateServiceablePopup(false));
          }}
          pageName="new_cars_web"
          isWeb
          leadGenerationSource={leadGenerationSource as any}
        />
      </div>
    </Modal>
  );
};

export default TestDrivePopup;
